import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export default function DataTable({ columns, data, loading, emptyText = 'No data found' }) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const handleSort = key => {
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const sorted = sort.key
    ? [...data].sort((a, b) => {
        const va = a[sort.key], vb = b[sort.key];
        const cmp = typeof va === 'number' ? va - vb : String(va ?? '').localeCompare(String(vb ?? ''));
        return sort.dir === 'asc' ? cmp : -cmp;
      })
    : data;

  if (loading) {
    return (
      <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
        <div className="p-4 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton h-8" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden border border-slate-100 shadow-card bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap select-none ${col.sortable ? 'cursor-pointer hover:text-primary-700' : ''}`}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && (
                      sort.key === col.key
                        ? sort.dir === 'asc'
                          ? <ChevronUp size={14} className="text-primary-600" />
                          : <ChevronDown size={14} className="text-primary-600" />
                        : <ChevronsUpDown size={14} className="text-slate-300" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={columns.length} className="py-12 text-center text-slate-400">{emptyText}</td></tr>
            ) : (
              sorted.map((row, i) => (
                <tr key={row.id ?? i} className={`border-b border-slate-50 hover:bg-primary-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                  {columns.map(col => (
                    <td key={col.key} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Status badge helper used across pages
export function StatusBadge({ status }) {
  const map = {
    present:       'bg-emerald-100 text-emerald-700',
    absent:        'bg-red-100 text-red-700',
    late:          'bg-amber-100 text-amber-700',
    half_day:      'bg-orange-100 text-orange-700',
    single_punch:  'bg-sky-100 text-sky-700',
    pending:       'bg-amber-100 text-amber-700',
    in_progress:   'bg-blue-100 text-blue-700',
    resolved:      'bg-emerald-100 text-emerald-700',
    closed:        'bg-slate-100 text-slate-600',
    completed:     'bg-emerald-100 text-emerald-700',
    planned:       'bg-purple-100 text-purple-700',
    not_completed: 'bg-red-100 text-red-700',
    published:     'bg-emerald-100 text-emerald-700',
    draft:         'bg-slate-100 text-slate-600',
    critical:      'bg-red-100 text-red-800',
    high:          'bg-orange-100 text-orange-700',
    medium:        'bg-amber-100 text-amber-700',
    low:           'bg-slate-100 text-slate-600',
  };
  const label = status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || '—';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${map[status] || 'bg-slate-100 text-slate-600'}`}>
      {label}
    </span>
  );
}
